/**
 * ElectroCarousel - Staff Level Modular Implementation
 * Lightweight, high-performance carousel engine for enterprise templates.
 */

class ElectroCarousel {
    constructor(containerSelector, options = {}) {
        this.container = typeof containerSelector === 'string' ? document.querySelector(containerSelector) : containerSelector;
        if (!this.container) return;

        this.wrapper = this.container.querySelector('.swiper-wrapper');
        this.slides = Array.from(this.wrapper.querySelectorAll('.swiper-slide'));
        
        this.options = {
            slidesPerView: 1,
            spaceBetween: 0,
            loop: false,
            autoplay: false,
            effect: 'slide',
            pagination: null,
            navigation: { nextEl: null, prevEl: null },
            breakpoints: {},
            ...options
        };

        this.currentIndex = 0;
        this.totalSlides = this.slides.length;
        this.autoPlayInterval = null;
        
        this.init();
    }

    init() {
        this.updateBreakpoints();
        this.setupStyles();
        this.setupNavigation();
        this.setupPagination();
        this.setupEvents();
        this.update();

        if (this.options.autoplay) this.startAutoplay();
        
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.container);
    }

    updateBreakpoints() {
        const width = window.innerWidth;
        this.currentOptions = { ...this.options };
        const breakpoints = Object.keys(this.options.breakpoints).map(Number).sort((a, b) => a - b);
        for (const bp of breakpoints) {
            if (width >= bp) Object.assign(this.currentOptions, this.options.breakpoints[bp]);
        }
    }

    setupStyles() {
        this.container.style.overflow = 'hidden';
        this.container.style.position = 'relative';
        
        if (this.currentOptions.effect === 'slide') {
            this.wrapper.style.display = 'flex';
            this.wrapper.style.transitionProperty = 'transform';
            this.wrapper.style.transitionDuration = '0.5s';
            this.slides.forEach(s => s.style.flexShrink = '0');
        } else if (this.currentOptions.effect === 'fade') {
            this.wrapper.style.position = 'relative';
            this.slides.forEach(s => {
                s.style.position = 'absolute';
                s.style.top = '0';
                s.style.left = '0';
                s.style.width = '100%';
                s.style.transition = 'opacity 0.8s ease';
            });
        }
    }

    update() {
        this.updateBreakpoints();
        if (this.currentOptions.effect === 'fade') return this.moveTo(this.currentIndex, true);

        const perView = this.currentOptions.slidesPerView;
        const containerWidth = this.container.offsetWidth;
        const gap = this.currentOptions.spaceBetween;
        
        this.slideWidth = (containerWidth - (gap * (perView - 1))) / perView;
        
        this.slides.forEach(s => {
            s.style.width = `${this.slideWidth}px`;
            s.style.marginRight = `${gap}px`;
        });
        
        this.moveTo(this.currentIndex, true);
    }

    moveTo(index, immediate = false) {
        const max = this.totalSlides - this.currentOptions.slidesPerView;
        if (index < 0) index = this.options.loop ? this.totalSlides - 1 : 0;
        if (index > max) index = this.options.loop ? 0 : max;

        this.currentIndex = index;

        if (this.currentOptions.effect === 'slide') {
            const offset = (this.slideWidth + this.currentOptions.spaceBetween) * this.currentIndex;
            this.wrapper.style.transitionDuration = immediate ? '0s' : '0.5s';
            this.wrapper.style.transform = `translateX(-${offset}px)`;
        } else {
            this.slides.forEach((s, i) => {
                s.style.opacity = i === this.currentIndex ? '1' : '0';
                s.style.zIndex = i === this.currentIndex ? '10' : '0';
            });
        }
        this.updatePagination();
    }

    next() { this.moveTo(this.currentIndex + 1); }
    prev() { this.moveTo(this.currentIndex - 1); }

    setupNavigation() {
        const { nextEl, prevEl } = this.options.navigation;
        if (nextEl) document.querySelector(nextEl)?.addEventListener('click', () => this.next());
        if (prevEl) document.querySelector(prevEl)?.addEventListener('click', () => this.prev());
    }

    setupPagination() {
        if (!this.options.pagination?.el) return;
        const el = document.querySelector(this.options.pagination.el);
        if (!el) return;
        
        el.innerHTML = '';
        const count = this.currentOptions.effect === 'fade' ? this.totalSlides : (this.totalSlides - this.currentOptions.slidesPerView + 1);
        
        for (let i = 0; i < count; i++) {
            const dot = document.createElement('span');
            dot.className = 'swiper-pagination-bullet';
            dot.onclick = () => this.moveTo(i);
            el.appendChild(dot);
        }
        this.updatePagination();
    }

    updatePagination() {
        if (!this.options.pagination?.el) return;
        const dots = document.querySelectorAll(`${this.options.pagination.el} .swiper-pagination-bullet`);
        dots.forEach((d, i) => d.classList.toggle('swiper-pagination-bullet-active', i === this.currentIndex));
    }

    startAutoplay() {
        this.autoPlayInterval = setInterval(() => this.next(), this.options.autoplay.delay || 3000);
    }

    setupEvents() {
        this.container.addEventListener('mouseenter', () => clearInterval(this.autoPlayInterval));
        this.container.addEventListener('mouseleave', () => this.options.autoplay && this.startAutoplay());
    }
}

// Export as Default Module for the Component Loader
export default (el) => {
    const configAttr = el.getAttribute('data-carousel-config');
    const config = configAttr ? JSON.parse(configAttr) : {};
    return new ElectroCarousel(el, config);
};
